import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { adminLogin, getContactForms, getJobApplications, getJobOpenings, getNewsletterSubscribers, getConsultations, getAdmins, createAdmin, updateAdmin, deleteAdmin, createJobOpening, updateJobOpening, deleteJobOpening } from "@/lib/api";

const tabs = [
  { key: "consultations", label: "Scheduled Consultations" },
  { key: "jobApplications", label: "Job Applications" },
  { key: "openings", label: "Current Openings" },
  { key: "subscribers", label: "Subscribers" },
  { key: "admins", label: "Admins" }
];

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [activeTab, setActiveTab] = useState("consultations");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [openingFormOpen, setOpeningFormOpen] = useState(false);
  const [openingForm, setOpeningForm] = useState({
    title: "",
    department: "",
    location: "",
    type: "",
    experience: "",
    description: "",
    requirements: ""
  });
  const [editingOpeningId, setEditingOpeningId] = useState(null);
  const [adminFormOpen, setAdminFormOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    email: "",
    password: "",
    role: "admin"
  });
  const [editingAdminEmail, setEditingAdminEmail] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const saved = localStorage.getItem("ssa_admin_authenticated") === "true";
    const savedAdmin = localStorage.getItem("ssa_admin_info");
    setAuthenticated(saved);
    if (savedAdmin) {
      try {
        setCurrentAdmin(JSON.parse(savedAdmin));
      } catch {
        setCurrentAdmin(null);
      }
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      setPage(1);
      loadData(activeTab);
    }
  }, [authenticated, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const adminInfo = await adminLogin(loginData.email, loginData.password);

      localStorage.setItem("ssa_admin_authenticated", "true");
      localStorage.setItem("ssa_admin_info", JSON.stringify(adminInfo));
      setAuthenticated(true);
      setCurrentAdmin(adminInfo);
      toast.success("Admin login successful.");
      setLoginData({ email: "", password: "" });
      setError(null);
    } catch (err) {
      toast.error("Invalid email or password.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("ssa_admin_authenticated");
    localStorage.removeItem("ssa_admin_info");
    setAuthenticated(false);
    setCurrentAdmin(null);
    setData([]);
    setError(null);
  };

  const resetOpeningForm = () => {
    setOpeningForm({
      title: "",
      department: "",
      location: "",
      type: "",
      experience: "",
      description: "",
      requirements: ""
    });
    setEditingOpeningId(null);
  };

  const getOpeningId = (opening) => opening?.id || opening?._id || null;

  const openNewOpening = () => {
    resetOpeningForm();
    setOpeningFormOpen(true);
  };

  const isSuperAdmin = currentAdmin?.role === "superadmin" || currentAdmin?.email === "contact@ssaca.in";

  const resetAdminForm = () => {
    setAdminForm({
      email: "",
      password: "",
      role: "admin"
    });
    setEditingAdminEmail(null);
  };

  const openNewAdmin = () => {
    resetAdminForm();
    setAdminFormOpen(true);
  };

  const handleEditAdmin = (admin) => {
    setAdminForm({
      email: admin.email || "",
      password: "",
      role: admin.role || "admin"
    });
    setEditingAdminEmail(admin.email);
    setAdminFormOpen(true);
  };

  const handleDeleteAdmin = async (email) => {
    if (!window.confirm("Delete this admin?")) return;
    if (!isSuperAdmin) {
      toast.error("Only the superadmin can delete admin accounts.");
      return;
    }
    setLoading(true);
    try {
      await deleteAdmin(email, currentAdmin.email);
      toast.success("Admin deleted successfully.");
      loadData("admins");
    } catch (err) {
      toast.error("Unable to delete the admin account.");
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      email: adminForm.email,
      password: adminForm.password,
      role: adminForm.role
    };

    try {
      if (editingAdminEmail) {
        await updateAdmin(editingAdminEmail, payload, currentAdmin.email);
        toast.success("Admin updated successfully.");
      } else {
        if (!isSuperAdmin) {
          throw new Error("Only superadmin can create new admins.");
        }
        await createAdmin(adminForm.email, adminForm.password, adminForm.role, currentAdmin.email);
        toast.success("Admin created successfully.");
      }
      setAdminFormOpen(false);
      resetAdminForm();
      loadData("admins");
    } catch (err) {
      toast.error(err.message || "Unable to save admin account.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpening = (opening) => {
    const requirementsValue = Array.isArray(opening.requirements)
      ? opening.requirements.join("\n")
      : typeof opening.requirements === "string"
        ? opening.requirements
        : "";

    setOpeningForm({
      title: opening.title || "",
      department: opening.department || "",
      location: opening.location || "",
      type: opening.type || "",
      experience: opening.experience || "",
      description: opening.description || "",
      requirements: requirementsValue
    });

    setEditingOpeningId(getOpeningId(opening));
    setOpeningFormOpen(true);
  };

  const handleDeleteOpening = async (openingId) => {
    if (!window.confirm("Delete this opening?")) return;
    setLoading(true);
    try {
      await deleteJobOpening(openingId);
      toast.success("Opening deleted successfully.");
      loadData("openings");
    } catch (err) {
      toast.error("Unable to delete the opening.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpeningSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      title: openingForm.title,
      department: openingForm.department,
      location: openingForm.location,
      type: openingForm.type,
      experience: openingForm.experience,
      description: openingForm.description,
      requirements: openingForm.requirements
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean)
    };

    try {
      if (editingOpeningId) {
        await updateJobOpening(editingOpeningId, payload);
        toast.success("Opening updated successfully.");
      } else {
        await createJobOpening(payload);
        toast.success("Opening created successfully.");
      }
      setOpeningFormOpen(false);
      resetOpeningForm();
      loadData("openings");
    } catch (err) {
      toast.error("Unable to save the opening.");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (tab) => {
    setLoading(true);
    setError(null);
    setData([]);

    try {
      let result;
      const adminEmail = currentAdmin?.email;

      switch (tab) {
        case "consultations":
          result = await getConsultations(adminEmail);
          break;
        case "jobApplications":
          result = await getJobApplications(adminEmail);
          break;
        case "openings":
          result = await getJobOpenings();
          break;
        case "subscribers":
          result = await getNewsletterSubscribers(adminEmail);
          break;
        case "admins":
          result = await getAdmins(adminEmail);
          break;
        default:
          result = await getContactForms(adminEmail);
      }

      setData(result || []);
    } catch (err) {
      setError("Failed to load data. Please check the backend.");
    } finally {
      setLoading(false);
    }
  };

  const PaginationControls = () => {
    const totalPages = Math.ceil(data.length / pageSize);
    if (totalPages <= 1) return null;

    const startIdx = (page - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const showing = `${startIdx + 1}–${Math.min(endIdx, data.length)} of ${data.length}`;

    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
        <p className="text-sm text-slate-600">{showing}</p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="border-slate-300 text-slate-700"
          >
            Previous
          </Button>
          <span className="flex items-center px-3 text-sm text-slate-600">
            {page} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
            className="border-slate-300 text-slate-700"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const paginatedData = data.slice((page - 1) * pageSize, page * pageSize);

  const renderTable = () => {
    if (loading) {
      return <p className="text-slate-600">Loading data...</p>;
    }

    if (error) {
      return <p className="text-red-600">{error}</p>;
    }

    if ((!data || data.length === 0) && activeTab !== "openings") {
      return <p className="text-slate-600">No records found for this tab.</p>;
    }

    if (activeTab === "consultations") {
      return (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-100">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Preferred Date</th>
                  <th className="px-4 py-3">Expert</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Created At</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={index} className="border-b border-slate-200 even:bg-slate-50">
                    <td className="px-4 py-3">{item.name}</td>
                    <td className="px-4 py-3">{item.email}</td>
                    <td className="px-4 py-3">{item.phone || "-"}</td>
                    <td className="px-4 py-3">{item.preferred_date || "-"}</td>
                    <td className="px-4 py-3">{item.expert_name || "-"}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{item.message}</td>
                    <td className="px-4 py-3">{new Date(item.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls />
        </>
      );
    }

    if (activeTab === "jobApplications") {
      return (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-100">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Position</th>
                  <th className="px-4 py-3">Experience</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Resume</th>
                  <th className="px-4 py-3">Cover Letter</th>
                  <th className="px-4 py-3">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={index} className="border-b border-slate-200 even:bg-slate-50">
                    <td className="px-4 py-3">{item.full_name}</td>
                    <td className="px-4 py-3">{item.email}</td>
                    <td className="px-4 py-3">{item.phone}</td>
                    <td className="px-4 py-3">{item.position}</td>
                    <td className="px-4 py-3">{item.experience}</td>
                    <td className="px-4 py-3">{item.current_company || "-"}</td>
                    <td className="px-4 py-3">
                      {item.resume_url ? (
                        <a href={item.resume_url} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:text-[#0B1120] underline text-sm">
                          View Resume
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate">{item.cover_letter || "-"}</td>
                    <td className="px-4 py-3">{new Date(item.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls />
        </>
      );
    }

    if (activeTab === "openings") {
      return (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-slate-600">Manage current openings listed on the Careers page.</p>
            </div>
            <Button onClick={openNewOpening} className="bg-[#0B1120] hover:bg-[#C5A059] text-white">
              New Opening
            </Button>
          </div>

          {openingFormOpen && (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-[#0B1120] mb-4">
                {editingOpeningId ? "Edit Opening" : "Create Opening"}
              </h3>
              <form onSubmit={handleOpeningSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    placeholder="Job Title"
                    value={openingForm.title}
                    onChange={(e) => setOpeningForm({ ...openingForm, title: e.target.value })}
                    required
                    className="h-14"
                  />
                  <Input
                    placeholder="Department"
                    value={openingForm.department}
                    onChange={(e) => setOpeningForm({ ...openingForm, department: e.target.value })}
                    required
                    className="h-14"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    placeholder="Location"
                    value={openingForm.location}
                    onChange={(e) => setOpeningForm({ ...openingForm, location: e.target.value })}
                    required
                    className="h-14"
                  />
                  <Input
                    placeholder="Employment Type"
                    value={openingForm.type}
                    onChange={(e) => setOpeningForm({ ...openingForm, type: e.target.value })}
                    required
                    className="h-14"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    placeholder="Experience"
                    value={openingForm.experience}
                    onChange={(e) => setOpeningForm({ ...openingForm, experience: e.target.value })}
                    required
                    className="h-14"
                  />
                </div>
                <Textarea
                  placeholder="Description"
                  value={openingForm.description}
                  onChange={(e) => setOpeningForm({ ...openingForm, description: e.target.value })}
                  required
                  rows={4}
                />
                <Textarea
                  placeholder="Requirements (one per line)"
                  value={openingForm.requirements}
                  onChange={(e) => setOpeningForm({ ...openingForm, requirements: e.target.value })}
                  rows={4}
                />
                <div className="flex flex-wrap gap-3">
                  <Button type="submit" className="bg-[#0B1120] hover:bg-[#C5A059] text-white">
                    {editingOpeningId ? "Save Changes" : "Create Opening"}
                  </Button>
                  <Button type="button" variant="outline" className="border-slate-300 text-slate-700" onClick={() => { setOpeningFormOpen(false); resetOpeningForm(); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-100">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Dept</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Experience</th>
                  <th className="px-4 py-3">Requirements</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => {
                  const openingId = getOpeningId(item) || index;
                  const requirements = Array.isArray(item.requirements)
                    ? item.requirements.join(", ")
                    : typeof item.requirements === "string"
                      ? item.requirements
                      : "";

                  return (
                    <tr key={openingId} className="border-b border-slate-200 even:bg-slate-50">
                      <td className="px-4 py-3">{item.title}</td>
                      <td className="px-4 py-3">{item.department}</td>
                      <td className="px-4 py-3">{item.location}</td>
                      <td className="px-4 py-3">{item.type}</td>
                      <td className="px-4 py-3">{item.experience}</td>
                      <td className="px-4 py-3 max-w-xs truncate">{requirements}</td>
                      <td className="px-4 py-3 space-x-2">
                        <Button type="button" onClick={() => handleEditOpening(item)} className="bg-[#C5A059] hover:bg-[#0B1120] text-white px-3 py-2 text-sm">
                          Edit
                        </Button>
                        <Button type="button" variant="outline" className="border-slate-300 text-slate-700 px-3 py-2 text-sm" onClick={() => handleDeleteOpening(openingId)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeTab === "admins") {
      return (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-slate-600">Manage admin accounts and credentials.</p>
            </div>
            {isSuperAdmin && (
              <Button onClick={openNewAdmin} className="bg-[#0B1120] hover:bg-[#C5A059] text-white">
                New Admin
              </Button>
            )}
          </div>

          {adminFormOpen && (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-[#0B1120] mb-4">
                {editingAdminEmail ? "Edit Admin" : "Create Admin"}
              </h3>
              <form onSubmit={handleAdminSubmit} className="space-y-4">
                <Input
                  placeholder="Admin Email"
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  required
                  className="h-14"
                  disabled={Boolean(editingAdminEmail)}
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                  required={!Boolean(editingAdminEmail)}
                  className="h-14"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <select
                    value={adminForm.role}
                    onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
                    className="h-14 rounded-xl border border-slate-200 px-4 text-slate-700"
                  >
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button type="submit" className="bg-[#0B1120] hover:bg-[#C5A059] text-white">
                    {editingAdminEmail ? "Save Changes" : "Create Admin"}
                  </Button>
                  <Button type="button" variant="outline" className="border-slate-300 text-slate-700" onClick={() => { setAdminFormOpen(false); resetAdminForm(); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-100">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Created At</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={item.email || index} className="border-b border-slate-200 even:bg-slate-50">
                    <td className="px-4 py-3">{item.email}</td>
                    <td className="px-4 py-3">{item.role || "admin"}</td>
                    <td className="px-4 py-3">{new Date(item.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 space-x-2">
                      {(isSuperAdmin || item.email === currentAdmin.email) && (
                        <Button type="button" onClick={() => handleEditAdmin(item)} className="bg-[#C5A059] hover:bg-[#0B1120] text-white px-3 py-2 text-sm">
                          Edit
                        </Button>
                      )}
                      {isSuperAdmin && item.email !== currentAdmin.email && (
                        <Button type="button" variant="outline" className="border-slate-300 text-slate-700 px-3 py-2 text-sm" onClick={() => handleDeleteAdmin(item.email)}>
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls />
        </div>
      );
    }

    return (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-100">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Subscribed At</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={index} className="border-b border-slate-200 even:bg-slate-50">
                  <td className="px-4 py-3">{item.email}</td>
                  <td className="px-4 py-3">{item.name || "-"}</td>
                  <td className="px-4 py-3">{new Date(item.subscribed_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls />
      </>
    );
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-50 py-24">
        <div className="max-w-xl mx-auto bg-white rounded-3xl border border-slate-200 p-10 shadow-lg">
          <div className="flex flex-col items-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_audit-experts-6/artifacts/e6e7ccut_3467b892-0442-4ffa-8cf9-3f36ffd8d74b-removebg-preview.png"
              alt="SSA Logo"
              className="h-16 w-auto object-contain mb-4"
            />
            <h1 className="text-3xl font-semibold text-[#0B1120]">Admin Login</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <Input
              placeholder="Email"
              type="email"
              value={loginData.email}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              required
              className="h-14"
            />
            <Input
              type="password"
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              required
              className="h-14"
            />
            <Button type="submit" className="w-full bg-[#0B1120] hover:bg-[#C5A059] text-white h-14">
              Sign In
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <header className="flex items-center justify-between py-6">
          <img
            src="https://customer-assets.emergentagent.com/job_audit-experts-6/artifacts/e6e7ccut_3467b892-0442-4ffa-8cf9-3f36ffd8d74b-removebg-preview.png"
            alt="SSA Admin"
            className="h-12 w-auto object-contain"
          />
        </header>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
          <div>
            <h1 className="text-4xl font-bold text-[#0B1120]">Admin Dashboard</h1>
            <p className="text-slate-600 mt-2">Review contact, job applications, and newsletter subscriber data.</p>
          </div>
          <Button onClick={handleLogout} className="bg-[#C5A059] hover:bg-[#0B1120] text-white">
            Logout
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
                activeTab === tab.key ? "bg-[#0B1120] text-white" : "bg-white text-slate-700 border border-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-[#0B1120] mb-4">
            {tabs.find((tab) => tab.key === activeTab)?.label}
          </h2>
          {renderTable()}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
